import React, { useEffect } from "react";
import { X, Download } from "lucide-react";

/**
 * HidroVerse Nelspruit (Pty) Ltd — Standard Terms & Conditions of Sale & Service.
 * Verbatim copy supplied by the company. Rendered as a scrollable navy/light
 * panel over a dimmed backdrop. Closes on backdrop click, X button, "Got it",
 * or Escape.
 */
/**
 * Open a clean standalone print window with just the T&C body content.
 * This avoids the overlapping-text problem caused by `visibility: hidden`
 * not removing elements from layout flow. The user can save as PDF or
 * print from this dedicated window.
 */
function printTermsToWindow() {
  const body = document.getElementById("hv-terms-body");
  if (!body) return;
  const win = window.open("", "_blank", "width=900,height=1000");
  if (!win) return;
  win.document.open();
  win.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>HidroVerse — Standard Terms &amp; Conditions</title>
  <style>
    @page { margin: 18mm 15mm; }
    * { box-sizing: border-box; }
    body {
      font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
      color: #1A1F25;
      font-size: 11pt;
      line-height: 1.55;
      margin: 0;
      padding: 24px;
      background: #FFFFFF;
    }
    .doc-header {
      border-bottom: 2px solid #0C2A40;
      padding-bottom: 14px;
      margin-bottom: 22px;
    }
    .eyebrow {
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 8pt;
      letter-spacing: 0.22em;
      text-transform: uppercase;
      color: #1FB6C1;
      font-weight: 700;
      margin-bottom: 4px;
    }
    .doc-header h1 {
      margin: 0;
      font-size: 18pt;
      color: #0C2A40;
      font-weight: 500;
      letter-spacing: -0.01em;
    }
    section { page-break-inside: avoid; margin-bottom: 16px; }
    section h3 {
      font-size: 12pt;
      color: #0C2A40;
      font-weight: 600;
      margin: 14px 0 6px;
    }
    section h3 .num { color: #1FB6C1; margin-right: 6px; }
    p { margin: 0 0 6px; }
    ul { margin: 4px 0 8px 22px; padding: 0; }
    li { margin-bottom: 2px; }
    strong { color: #0C2A40; font-weight: 600; }
    .callout {
      background: #FAFAF7;
      border-left: 2px solid #1FB6C1;
      padding: 10px 14px;
      margin-top: 14px;
      font-size: 10pt;
    }
    .footer {
      margin-top: 28px;
      padding-top: 12px;
      border-top: 1px solid #E5E7EB;
      font-size: 9pt;
      color: #6B7280;
      display: flex;
      justify-content: space-between;
    }
    @media print {
      body { padding: 0; }
      .no-print { display: none !important; }
    }
  </style>
</head>
<body>
  <div class="doc-header">
    <div class="eyebrow">HidroVerse Nelspruit (Pty) Ltd</div>
    <h1>Standard Terms &amp; Conditions of Sale &amp; Service</h1>
  </div>
  ${body.innerHTML
    .replace(/<h3[^>]*>/g, '<h3>')
    .replace(/<div[^>]*color: rgb\(31, 182, 193\)[^"]*"[^>]*>([^<]+)<\/div>/g, '<span class="num">$1</span>')}
  <div class="footer">
    <span>HidroVerse Nelspruit (Pty) Ltd · Reg. 2019/371687/07</span>
    <span>Waterfall Park, Unit C1, Waterfall Avenue, Nelspruit, 1200</span>
  </div>
  <script>
    window.addEventListener('load', function() {
      setTimeout(function() { window.print(); }, 200);
    });
  </script>
</body>
</html>`);
  win.document.close();
}

export default function TermsModal({ open, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-modal-title"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
      data-testid="terms-modal"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close terms"
        onClick={onClose}
        className="absolute inset-0"
        style={{ background: "rgba(7,10,13,0.78)", backdropFilter: "blur(6px)" }}
        data-testid="terms-modal-backdrop"
      />

      {/* Panel */}
      <div
        className="relative w-full max-w-3xl max-h-[88vh] overflow-hidden flex flex-col"
        style={{
          background: "#FFFFFF",
          borderRadius: 12,
          boxShadow: "0 32px 80px -16px rgba(0,0,0,0.6)",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 md:px-8 py-5"
          style={{ background: "#0C2A40", color: "#FFFFFF" }}
        >
          <div>
            <div
              className="font-mono uppercase mb-1"
              style={{ fontSize: "0.6rem", letterSpacing: "0.28em", color: "#1FB6C1", fontWeight: 700 }}
            >
              HidroVerse Nelspruit (Pty) Ltd
            </div>
            <h2
              id="terms-modal-title"
              className="font-display"
              style={{ fontSize: "1.2rem", letterSpacing: "-0.01em", fontWeight: 500 }}
            >
              Standard Terms &amp; Conditions
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full transition-colors hover:bg-white/10"
            aria-label="Close"
            data-testid="terms-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body — scrolls */}
        <div
          id="hv-terms-body"
          className="overflow-y-auto px-6 md:px-8 py-6 space-y-6 font-light"
          style={{ fontSize: "0.94rem", lineHeight: 1.6, color: "#1A1F25" }}
        >
          <Clause n="1" title="Application of Terms and Conditions">
            <p>
              1.1 These terms and conditions deem to be accepted and agreed to, without any
              exclusions, by the Customer upon:
            </p>
            <ul className="list-disc pl-5 mt-1 space-y-1">
              <li>1.1.1 Notifying the Supplier of acceptance in writing; or</li>
              <li>1.1.2 Signing the terms and conditions; or</li>
              <li>1.1.3 Paying a deposit of any amount; or</li>
              <li>1.1.4 Accepting an offer from the Supplier.</li>
            </ul>
            <p>
              1.2 The Supplier shall supply, and the Customer shall purchase, the Goods and
              Services in accordance with the quotation confirmation which shall be subject
              to these Terms and Conditions.
            </p>
            <p>
              1.3 The Contract shall be to the exclusion of any other terms and conditions
              subject to which any such quotation is accepted or purported to be accepted, or
              any such order is made or purported to be made, by the Customer.
            </p>
          </Clause>

          <Clause n="2" title="Definitions and Interpretation">
            <Defn term="Business Day">any day other than a Saturday, Sunday or public holiday in the Republic of South Africa.</Defn>
            <Defn term="Commencement Date">the commencement date for the Contract as set out in the specified sales documentation.</Defn>
            <Defn term="Confidential Information">any information disclosed by one Party to the other in connection with this Agreement, whether orally, in writing or in any other medium.</Defn>
            <Defn term="Contract">the contract for the purchase and sale of the Goods and supply of the Services under these Terms and Conditions.</Defn>
            <Defn term="Contract Price">the price stated in the sales documentation for the Goods and/or Services.</Defn>
            <Defn term="Customer">the person or entity who accepts a quotation or offer of the Supplier, or whose order is accepted by the Supplier.</Defn>
            <Defn term="Delivery Date">the date on which the Goods are delivered, supplied or installed, or the Services rendered, as communicated in writing.</Defn>
            <Defn term="Goods">the goods (including any installation) which the Supplier is to supply.</Defn>
            <Defn term="Services">the Services to be provided to the Customer as set out in the specified sales documentation.</Defn>
            <Defn term="Supplier">HidroVerse Nelspruit (Pty) Ltd, registration number 2019/371687/07, Waterfall Park, Unit C1, Waterfall Avenue, Nelspruit, 1200.</Defn>
          </Clause>

          <Clause n="3" title="Basis of Sale and Service">
            <p>3.1 The Supplier's employees or agents are not authorised to make any representations concerning the Goods or Services unless confirmed in writing. The Customer acknowledges that it does not rely on, and waives any claim for breach of, any such representations which are not so confirmed.</p>
            <p>3.2 No variation to these Terms and Conditions shall be binding unless agreed in writing between the authorised representatives of the Customer and the Supplier.</p>
            <p>3.3 Sales literature, price lists and price tags are subject to alteration without notice and do not constitute offers to sell. No contract shall be binding on the Supplier unless the Supplier has issued a written acceptance, delivered the Goods, provided the Services or issued an invoice — whichever is earlier.</p>
            <p>3.4 Any typographical, clerical or accidental errors in any sales literature, quotation, price list or invoice shall be subject to correction without liability on the Supplier's part.</p>
            <p>3.5 The Supplier may gather material including photos and videos of Goods and Services at the Customer's premises for marketing and other use as it deems fit, unless otherwise stated by the Customer in writing.</p>
          </Clause>

          <Clause n="4" title="The Goods">
            <p>4.1 No order submitted by the Customer shall be deemed accepted unless and until confirmed in writing by the Supplier.</p>
            <p>4.2 The specification for the Goods shall be that set out in the Supplier's sales documentation.</p>
            <p>4.3 The Goods and the installation thereof shall be limited to those expressly set out in the sales documentation.</p>
            <p>4.4 Illustrations, photographs or descriptions are intended as a guide only and shall not be binding on the Supplier.</p>
            <p>4.5 The Supplier shall not be liable for any damages or changes to property that are a direct result of and part of the nature of the Goods being delivered or installed. It is the Customer's responsibility to ensure the necessary measures are in place to prevent damage.</p>
            <p>4.6 The Supplier reserves the right to make changes to conform with safety or regulatory requirements, or where changes are outside the Supplier's control.</p>
            <p>4.7 No accepted order may be cancelled by the Customer except with the Supplier's written agreement, and the Customer shall indemnify the Supplier against all loss (including loss of profit), costs, damages and expenses incurred as a result of the cancellation.</p>
            <p>4.8 The Supplier reserves the right to withhold Goods in part or in full should factors beyond the Supplier's control materially change the Goods or Price.</p>
          </Clause>

          <Clause n="5" title="The Services">
            <p>5.1 With effect from the Commencement Date the Supplier shall, subject to payment, provide the Services expressly identified in the quotation or specified schedule.</p>
            <p>5.2 The Supplier will use reasonable care and skill to perform the Services.</p>
            <p>5.3 The Supplier shall use its reasonable endeavours to complete the Services but time shall not be of the essence.</p>
            <p>5.4 The Supplier shall not be liable for damages or changes to property that are a direct result of and part of the nature of Services being rendered. It is the Customer's responsibility to ensure necessary measures are in place to prevent damage.</p>
            <p>5.5 The Services shall be limited to those expressly set out in the sales documentation.</p>
            <p>5.6 The Supplier reserves the right to make changes to conform with safety or regulatory requirements.</p>
            <p>5.7 The Supplier reserves the right to withhold Services where factors beyond its control materially change the Service or Price.</p>
            <p>5.8 No accepted order may be cancelled except with the Supplier's written agreement; cancellation indemnity applies as per Clause 4.7.</p>
            <p>5.9 It is the Customer's responsibility to ensure all site-specific information (access, security, sanitary, safety, emergency, electrical / water connections etc.) is disclosed to the Supplier. Failure to do so may result in involuntary amendments to Goods, Services, Price and Delivery.</p>
          </Clause>

          <Clause n="6" title="Price">
            <p>6.1 The price of the Goods and Services shall be the price listed in the Supplier's quotation current at the date of acceptance of the Customer's order, or such other price as may be agreed in writing.</p>
            <p>6.2 Where the Supplier has quoted a price for the Goods this shall be valid for 30 (thirty) days only or such lesser time as the Supplier may specify.</p>
            <p>6.3 The Supplier reserves the right, by giving written notice before delivery, to increase the price to reflect any increase in cost due to factors beyond the Supplier's control (foreign-exchange fluctuation, duty changes, material-cost increases, etc.).</p>
            <p>6.4 Unless stated otherwise, all prices are inclusive of packaging and transport charges.</p>
            <p>6.5 The price is exclusive of VAT, excise, sales taxes or similar levies, which the Customer shall be additionally liable to pay.</p>
          </Clause>

          <Clause n="7" title="Payment">
            <p>7.1 The Customer shall pay a deposit of 70% of the quotation Price unless otherwise stated in writing.</p>
            <p>7.2 Subject to any special terms agreed in writing, the Supplier shall invoice the Customer on or after delivery or provision.</p>
            <p>7.3 The Customer shall pay the balance of the Price stipulated on the quotation in accordance with the sales documentation.</p>
            <p>7.4 All payments shall be made to the Supplier as indicated on the quotation, invoice or other sales documents.</p>
            <p>7.5 The Supplier is not obliged to accept orders from a Customer who has not supplied satisfactory references. The Supplier may suspend further supply at any time if creditworthiness becomes unsatisfactory, and all outstanding amounts shall become immediately payable.</p>
            <p>7.6 The Customer is obligated to pay all Goods and Services regardless of whether they are satisfactory to the Customer, the only measure of fulfilment being the quotation and sales documents.</p>
          </Clause>

          <Clause n="8" title="Delivery and Performance">
            <p>8.1 Delivery of Goods and Services shall be made at the place specified in the quotation, or by collection from the Supplier's premises after notification.</p>
            <p>8.2 The Delivery Date is approximate only and time shall not be of the essence unless previously agreed in writing. Goods/Services may be delivered in advance or after the Delivery Date with reasonable notice.</p>
            <p>8.3 It is the Customer's responsibility to ensure the delivery location is ready to accept delivery without delay. Any delays not on the Supplier's side may lead to changes in Goods, Services and Price.</p>
            <p>8.4 Failure by the Customer to take delivery shall entitle the Supplier to store the Goods, pass risk to the Customer, and recover all storage and insurance costs.</p>
          </Clause>

          <Clause n="9" title="Non-Delivery">
            <p>9.1 If the Supplier fails to deliver the Goods or provide the Services on the Delivery Date, other than for reasons outside its reasonable control or the Customer's fault, and the Supplier delivers at any time thereafter, the Supplier shall have no liability for such late delivery.</p>
          </Clause>

          <Clause n="10" title="Risk and Retention of Title">
            <p>10.1 Risk of damage or loss passes to the Customer upon notification of readiness for collection, upon delivery, or upon installation completion (as applicable).</p>
            <p>10.2 Notwithstanding delivery and passing of risk, legal and beneficial title shall not pass until the Supplier has received payment in full of all amounts owed by the Customer — irrespective of how such indebtedness arose.</p>
            <p>10.3 The Customer shall not pledge or charge Goods that remain the Supplier's property. Any such attempt shall render all amounts owing immediately due and payable.</p>
            <p>10.4 The Supplier reserves the right to repossess any Goods to which it retains title without notice, and is irrevocably authorised to enter the Customer's premises during business hours to do so.</p>
          </Clause>

          <Clause n="11" title="Assignment">
            <p>11.1 The Supplier may assign the Contract without the Customer's consent.</p>
            <p>11.2 The Customer shall not be entitled to assign the Contract without the Supplier's prior written consent.</p>
          </Clause>

          <Clause n="12" title="Defective Goods">
            <p>12.1 If Goods are defective on delivery and the Customer refuses delivery or gives written notice of the defect within 7 (seven) Business Days, the Supplier shall at its option replace the Goods within 14 Business Days or refund the price. The Supplier shall have no further liability.</p>
            <p>12.1.3 Non-Returnable Items — No electrical components may be returned once delivered, except where defective upon delivery. Defective electrical components must be reported within 7 Business Days.</p>
            <p>12.2 No Goods may be returned without prior written agreement. Goods returned that were supplied with defects not apparent on inspection shall either be replaced free of charge or refunded at the Supplier's discretion.</p>
            <p>12.3 The Supplier shall have no liability for defects arising from fair wear and tear, wilful damage, negligence, failure to follow instructions, misuse or unauthorised alteration.</p>
            <p>12.6 All warranties, conditions and other terms implied by statute or common law are excluded to the fullest extent permitted by law (except where the Goods are sold under a consumer sale governed by the Consumer Protection Act).</p>
            <p>12.7 The Customer shall indemnify the Supplier against any liability arising from the Customer's handling or sale of the Goods.</p>
          </Clause>

          <Clause n="13" title="Customer's Default">
            <p>13.1 If the Customer fails to make any payment on the due date, the Supplier shall be entitled to (i) cancel or suspend further supply, (ii) appropriate payments to any outstanding Goods/Services, and (iii) charge interest at 8.5% per annum above the prevailing prime rate until payment in full.</p>
            <p>13.2 The above remedies also apply if the Customer breaches any obligation, becomes insolvent, is placed under administration, ceases business, or the Supplier reasonably apprehends any such event.</p>
            <p>13.3 In such events the Supplier may cancel the Contract without liability; all delivered but unpaid Goods shall become immediately due and payable.</p>
          </Clause>

          <Clause n="14" title="Liability">
            <p>14.1 The Supplier shall not be liable for any loss of profit, indirect, special or consequential loss, damage, costs, expenses or claims arising out of or in connection with the supply of Goods and Services.</p>
            <p>14.2 All warranties, conditions and other terms implied by statute or common law are excluded to the fullest extent permitted by law.</p>
            <p>14.3 The Customer shall indemnify the Supplier against all damages, costs, charges and expenses arising from any act or omission of the Customer.</p>
            <p>14.5 The Supplier shall not be liable for any delay or failure due to causes beyond its reasonable control.</p>
            <p>14.6 Nothing in these Terms excludes or limits the Supplier's liability for (i) death or personal injury caused by negligence, (ii) any matter which cannot lawfully be excluded, or (iii) fraud or fraudulent misrepresentation.</p>
            <p>14.7 The Supplier's total aggregate liability under the Contract shall be limited to the Contract Price.</p>
          </Clause>

          <Clause n="15" title="Confidentiality">
            <p>15.1 Each Party shall keep confidential all Confidential Information of the other, both during the Contract and for 6 (six) months after termination, and shall not make copies or disclose the information except as expressly permitted.</p>
            <p>15.2 Disclosure is permitted to subcontractors, authorities or advisors strictly for purposes of the Contract, subject to written confidentiality undertakings.</p>
          </Clause>

          <Clause n="16" title="Intellectual Property">
            <p>16.1 Each Party retains the Intellectual Property Rights owned prior to the Contract.</p>
            <p>16.2 Royalty-free, non-exclusive licences are granted only to the extent required to complete the Contract and terminate on termination of the Contract.</p>
            <p>16.3 The Supplier shall own all Intellectual Property Rights in documents, Goods and Services created during the Contract.</p>
            <p>16.5 The Customer receives a non-exclusive, non-transferable licence to use the Supplier's software solely to operate the Goods supplied.</p>
            <p>16.6 The Customer shall not copy, adapt, reverse-engineer, sub-licence or remove proprietary notices from any software or hardware.</p>
          </Clause>

          <Clause n="17" title="Communications">
            <p>17.1 All notices shall be in writing and signed by, or on behalf of, an authorised officer of the Party giving the notice.</p>
            <p>17.2 Notices are duly given when delivered by courier during business hours, or when transmitted by email with a successful transmission report or return receipt.</p>
            <p>17.3 Notices shall be addressed to the most recent address or email notified to the other Party.</p>
          </Clause>

          <Clause n="18" title="Force Majeure">
            <p>Neither Party shall be liable for any failure or delay resulting from causes beyond its reasonable control, including but not limited to: power failure, ISP failure, industrial action, civil unrest, fire, flood, storms, earthquakes, acts of terrorism, acts of war, governmental action or any other event beyond the Party's control.</p>
          </Clause>

          <Clause n="19" title="Waiver, Severance &amp; Third Party Rights">
            <p>No failure by either Party to enforce any provision shall constitute a waiver.</p>
            <p>If one or more provisions are found to be unlawful, invalid or unenforceable, those provisions shall be deemed severed; the remainder shall remain valid and enforceable.</p>
            <p>A person who is not a party to the Contract shall have no rights under it.</p>
          </Clause>

          <Clause n="20" title="Law and Jurisdiction">
            <p>20.1 These Terms and Conditions and the Contract shall be governed by and construed in accordance with the laws of the Republic of South Africa.</p>
            <p>20.2 Any dispute, controversy, proceedings or claim shall fall within the exclusive jurisdiction of the courts of the Republic of South Africa.</p>
          </Clause>

          <Clause n="21" title="Warranties">
            <p>21.1 The Supplier shall pass back-to-back any manufacturer warranty onto the Customer.</p>
            <p>21.2 Where proof of purchase is required, the Supplier shall provide this directly to the manufacturer.</p>
          </Clause>

          <Clause n="22" title="Consumer Protection &amp; PoPIA (Statutory addendum)">
            <p>22.1 Nothing in these Terms shall be construed as excluding or limiting any rights afforded to a Consumer under the Consumer Protection Act 68 of 2008 where the Customer qualifies as a Consumer under that Act.</p>
            <p>22.2 Pursuant to section 103(5) of the National Credit Act 34 of 2005, interest, fees and charges on any unpaid amount shall not exceed, in aggregate, the unpaid principal debt at the time of default.</p>
            <p>22.3 By submitting this application the Customer consents, in terms of the Protection of Personal Information Act 4 of 2013 (PoPIA), to the Supplier processing the personal information supplied for the purpose of evaluating creditworthiness, opening an account, contacting trade and credit bureaux, and complying with any statutory obligation.</p>
            <p>22.4 The Customer acknowledges that electronic signatures and electronic acceptance under the Electronic Communications and Transactions Act 25 of 2002 are binding for the purposes of this application.</p>
            <p>22.5 The Supplier shall keep personal information only for as long as is reasonably required to fulfil the purposes above, thereafter destroying or de-identifying it in accordance with PoPIA.</p>
          </Clause>

          <div
            className="p-4 mt-2"
            style={{ background: "#FAFAF7", borderLeft: "2px solid #1FB6C1", borderRadius: 4 }}
          >
            <p>
              <strong style={{ color: "#0C2A40", fontWeight: 600 }}>Need a signed copy?</strong>{" "}
              Email{" "}
              <a href="mailto:nelspruit@hidroverse.co.za" style={{ color: "#1FB6C1", fontWeight: 600 }}>
                nelspruit@hidroverse.co.za
              </a>{" "}
              and we'll send you a PDF on letterhead.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-2 px-6 md:px-8 py-4"
          style={{ borderTop: "1px solid rgba(12,42,64,0.08)", background: "#FAFAF7" }}
        >
          <button
            type="button"
            onClick={printTermsToWindow}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold transition-colors duration-200"
            style={{
              background: "#FFFFFF",
              color: "#0C2A40",
              fontSize: "0.74rem",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              border: "1px solid rgba(12,42,64,0.16)",
            }}
            data-testid="terms-modal-download"
            aria-label="Download Terms and Conditions as PDF"
          >
            <Download size={13} aria-hidden /> Download PDF
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2.5 rounded-full font-semibold transition-colors duration-200"
            style={{
              background: "#1FB6C1",
              color: "#070A0D",
              fontSize: "0.74rem",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
            data-testid="terms-modal-done"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Internal helpers ---
function Clause({ n, title, children }) {
  return (
    <section>
      <h3
        className="font-display mb-2"
        style={{ fontSize: "1.05rem", color: "#0C2A40", fontWeight: 600, letterSpacing: "-0.005em" }}
      >
        <span style={{ color: "#1FB6C1" }}>{n}.</span> {title}
      </h3>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function Defn({ term, children }) {
  return (
    <p>
      <strong style={{ color: "#0C2A40", fontWeight: 600 }}>"{term}"</strong> means {children}
    </p>
  );
}
